import { useState, useCallback, useRef } from 'react'
import { Chip, Box, useTheme, Button } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { Repeat } from '@mui/icons-material'
import { COLLAPSED_ROW_HEIGHT, EXPANDED_ROW_HEIGHT } from './crop-timeline/timeline-constants'
import { useFeatures } from '~/common/use-features'
import { TpTooltip } from '~/components'

const ICON_SHOW_DELAY = 200
const MAX_CHIP_WIDTH_PX = 200
const CHIP_ROW_GAP_PX = 0.6

export const CropScheduleLabelChip = ({
  isExpanded,
  isParentOrgHovered,
  label,
  notes,
  hasSuccession = false,
  onClick,
  onEdit,    // Optional override: replaces modal open (e.g. for org task rows)
  onDelete,  // Optional override: replaces modal open (e.g. for org task rows)
  disableActionButtons = false,
  readOnly = false,  // When true: desaturated + dashed border styling
  isEditing = false,  // When true: Edit button shows Check icon (submit)
  submitDisabled = false,  // When true: submit (Check) button is disabled
  ...chipProps
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [isHovered, setIsHovered] = useState(false)
  const hideTimeoutRef = useRef(null)
  const { features } = useFeatures()

  // Blank handler functions (to be implemented by user later)
  const handleEdit = useCallback((e) => {
    e.stopPropagation()
    onEdit && onEdit(e)
  }, [onEdit])

  const handleDelete = useCallback((e) => {
    e.stopPropagation()
    onDelete && onDelete(e)
  }, [onDelete])

  const handleMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    // Add delay before hiding icons
    hideTimeoutRef.current = setTimeout(() => setIsHovered(false), ICON_SHOW_DELAY) 
  }, [])

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: 'fit-content',
      }}
    >
      <TpTooltip
        title={isExpanded && notes ? notes : ''}
        enterDelay={1000}
        slotProps={{
          popper: {
            sx: {
              '& .MuiTooltip-tooltip': {
                whiteSpace: 'pre-wrap',
                maxWidth: MAX_CHIP_WIDTH_PX * 2,
              }
            }
          }
        }}
      >
        <Chip
          label={
            hasSuccession ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Repeat sx={{ fontSize: 18, flexShrink: 0 }}/>
                {label}
              </Box>
            ) : label
          }
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...chipProps}
          sx={{
            backgroundColor: readOnly
              ? isDark ? theme.palette.grey[800] : theme.palette.grey[200]
              : isParentOrgHovered
                ? isDark ? theme.palette.primary[700] : theme.palette.primary.main
                : isExpanded
                  ? isDark ? theme.palette.primary[800] : theme.palette.primary.light
                  : isDark ? theme.palette.primary[900] : theme.palette.background.paper,

            color: readOnly
              ? theme.palette.text.disabled
              : isParentOrgHovered || isExpanded || isDark
                ? theme.palette.primary.contrastText
                : theme.palette.text.primary,

            mb: isExpanded ? 0 : '1.75px',

            height: isExpanded ? EXPANDED_ROW_HEIGHT - 5 : COLLAPSED_ROW_HEIGHT - 4,

            border: readOnly
              ? `1px dashed ${theme.palette.text.disabled}`
              : isExpanded
                ? `2px solid ${theme.palette.primary.light}`
                : `0.25px solid ${theme.palette.primary.dark}`,

            borderRadius: '8px',
            cursor: readOnly ? 'default' : 'pointer',
            width: 'auto',
            maxWidth: MAX_CHIP_WIDTH_PX,
            justifyContent: 'flex-start',
            position: 'sticky',
            left: '0.5rem',
            pointerEvents: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            marginLeft: '0.5rem',

            transition: 'none', // to match the svg rects

            '&:hover': readOnly ? {} : {
              backgroundColor: isExpanded
                ? isDark ? theme.palette.primary[600] : theme.palette.primary.main
                : isDark ? theme.palette.primary[700] : theme.palette.primary[400],

              color: theme.palette.primary.contrastText,
              borderColor: theme.palette.primary.dark,
            },

            '& .MuiChip-label': {
              display: 'block',
              width: '100%',
              whiteSpace: isExpanded ? 'normal' : 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: isExpanded ? '3px 0px' : '1px 0',
            },
            
            ...chipProps?.sx,
          }}
        />
      </TpTooltip>

        {/* Action icons (Edit/Delete) - positioned outside chip to the right */}
        {!disableActionButtons && isExpanded && features['edit_crops'] && (
          <Box
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'absolute',
              left: '100%',  // Position right after chip
              top: '50%',
              height: EXPANDED_ROW_HEIGHT,
              pl: 1,  // 8px gap
              display: 'grid',
              gridTemplateRows: '1fr 1fr',
              gap: 0.5,
              zIndex: 40,  // Above timeline content
              pointerEvents: 'auto',
              py: CHIP_ROW_GAP_PX / 2,
              opacity: isHovered || isEditing ? 1 : 0,
              transform: isHovered || isEditing ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-12px)',
              transition: `opacity ${ICON_SHOW_DELAY}ms, transform ${ICON_SHOW_DELAY}ms`,
              '& button': {
                minWidth: '40px',
                px: 0,
                pointerEvents: isHovered || isEditing ? 'auto' : 'none',
              },
            }}
          >
            <Button
              size="small"
              onClick={handleEdit}
              onMouseEnter={handleMouseEnter}
              disabled={isEditing && submitDisabled}
              sx={{
                bgcolor: 'primary.light',
                boxShadow: 1,
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flex: 1,
                border: `2px solid ${isEditing ? 'success.dark' : 'primary.main'}`,

                '&:hover': {
                  bgcolor: isEditing ? 'success.dark' : 'primary.main'
                },
                '&:disabled': {
                  bgcolor: theme.palette.grey[400],
                  borderColor: theme.palette.grey[500],
                },
              }}
            >
              {isEditing
                ? <Icons.Check sx={{ fontSize: 16, color: 'success.contrastText' }} />
                : <Icons.Edit sx={{ fontSize: 16 }} />
              }
            </Button>
            <Button
              size="small"
              onClick={handleDelete}
              onMouseEnter={handleMouseEnter}
              sx={{
                bgcolor: isEditing ? 'warning.main' : 'error.main',
                boxShadow: 1,
                '&:hover': { bgcolor: isEditing ? 'warning.dark' : 'error.800' },
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flex: 1,
                border: `2px solid ${isEditing ? theme.palette.warning.dark : theme.palette.error[800]}`,
              }}
            >
              {isEditing
                ? <Icons.Clear sx={{ fontSize: 16 }} />
                : <Icons.Delete sx={{ fontSize: 16 }} />
              }
            </Button>
          </Box>
        )}
    </Box>
  )
}

